/**
 * Sidebar — DS-05 / DS-06.
 *
 * A fixed-width dark rail on the reading edge (RTL puts it on the right, DS-50),
 * grouped by micro-heading, with a live badge on every screen that holds a queue
 * of unfinished work. It scrolls independently of the content area: DS-01 says
 * the page itself never scrolls.
 *
 * Items the user has no permission for are not rendered at all (SEC-01) — a
 * greyed-out row would only advertise a screen they can never open.
 */

import { forwardRef, useMemo } from 'react';
import type { HTMLAttributes } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { api } from '@/lib/api';
import { NOTIFICATION_COUNTS_KEY } from '@/lib/query-client';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useLang } from '@/i18n';
import { Avatar } from '@/components/ui';
import { NAV_GROUPS } from './navigation';
import type { NavGroup, NavItem } from './navigation';

/** Shape of `GET /notifications/counts`; keyed by `NavItem.countKey`. */
type NotificationCounts = Record<string, number>;

/**
 * A badge turns red only where the number means work that is already late:
 * an unclosed appointment is an uninvoiced session (APT-08) and an open stock
 * alert is expiring or missing stock (INV-101). Everything else is a worklist,
 * and a rail of red chips stops being read at all.
 */
const URGENT_COUNT_KEYS = new Set<string>(['needsClosure', 'stockAlerts']);

/**
 * Longest registered prefix wins, which is the only rule that gets both cases
 * right: `/patients/:id` highlights ملفات المرضى, while `/patients/recalls`
 * highlights متابعات rather than both. `NavLink` decides per link and so cannot
 * express it — hence a plain `Link` and our own `aria-current`.
 */
function resolveActiveRoute(pathname: string): string | null {
  let best: string | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const matches = pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (best === null || item.to.length > best.length)) best = item.to;
    }
  }
  return best;
}

/**
 * Label for the roles a principal holds, in the reader's language. Shared with
 * the mobile drawer's identity block.
 *
 * PRD §3 — only the six seeded roles are translated: a user-built role already
 * carries the name its author gave it, so an unknown slug falls through to
 * itself rather than being blanked out.
 */
export function sidebarRoleLabel(
  roleNames: readonly string[] | undefined | null,
  t: TFunction<'common'>,
): string {
  return (
    roleNames
      ?.map((role) => (t(`roles.${role}`, { defaultValue: role }) as string))
      .join(' · ') ?? ''
  );
}

/**
 * The permission-filtered navigation tree, its badge counts and the route that
 * is currently active.
 *
 * NFR-05 — the phone renders the same tree inside an off-canvas drawer instead
 * of a rail. Both read it from here, so a screen a role cannot open is missing
 * from both, and the one `/notifications/counts` request feeds both sets of
 * badges rather than being fired twice for two copies of the same list.
 */
export function useNavigationTree(): {
  groups: NavGroup[];
  counts: NotificationCounts | undefined;
  activeTo: string | null;
} {
  const user = useAuthStore((state) => state.user);
  const canAny = useAuthStore((state) => state.canAny);
  const { pathname } = useLocation();
  const activeTo = useMemo(() => resolveActiveRoute(pathname), [pathname]);

  // One request feeds every badge in the rail (DS-05). It is deliberately
  // tolerant: the endpoint is optional infrastructure and a 404 or a 403 must
  // leave the navigation itself working, just without numbers.
  const { data: counts } = useQuery<NotificationCounts>({
    queryKey: NOTIFICATION_COUNTS_KEY,
    queryFn: () => api.get<NotificationCounts>('/notifications/counts').then((r) => r.data),
    enabled: Boolean(user),
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: false,
  });

  const groups = useMemo(() => {
    // Nothing is navigable before a session exists, and `canAny` reads the
    // principal out of the store rather than closing over it — so the filtered
    // tree is keyed on `user` and rebuilt whenever the principal changes.
    if (!user) return [];
    return NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => isPermitted(item, canAny)),
    })).filter((group) => group.items.length > 0);
  }, [canAny, user]);

  return { groups, counts, activeTo };
}

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** DS-06 — shown under the user's name; the clinic has one branch today. */
  clinicName?: string;
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { clinicName, className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const lang = useLang();
  const user = useAuthStore((state) => state.user);
  const { groups, counts, activeTo } = useNavigationTree();
  const roleLabel = sidebarRoleLabel(user?.roleNames, t);
  const clinic = clinicName ?? t('clinicName');

  return (
    <aside
      ref={ref}
      // NFR-05 — below 1024px the rail would eat a third of the viewport, so the
      // same tree moves into `MobileNav`. `max-lg:hidden` and nothing else: the
      // desktop rail keeps every class it had.
      className={cn('flex h-full w-60 shrink-0 flex-col bg-slate-800 max-lg:hidden', className)}
      {...rest}
    >
      <nav aria-label={t('a11y.mainNav')} className="flex-1 overflow-y-auto py-1">
        {groups.map((group) => (
          <div key={group.key} className="pb-1">
            {/* DS-12 */}
            <h2 className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              {lang === 'EN' ? group.labelEn : group.labelAr}
            </h2>

            <ul>
              {group.items.map((item) => (
                <li key={item.key}>
                  <SidebarLink
                    item={item}
                    active={activeTo === item.to}
                    count={item.countKey ? counts?.[item.countKey] : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* DS-06 — who am I, what am I allowed to be, and where am I. */}
      {user ? (
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-700 px-3 py-2.5">
          <Avatar name={user.fullNameAr} size="sm" className="bg-slate-600 text-slate-100" />
          <div className="min-w-0 flex-1 text-[11px] leading-tight">
            <div className="truncate font-bold text-slate-100" title={user.fullNameAr}>
              {user.fullNameAr}
            </div>
            <div className="truncate text-slate-400" title={`${roleLabel} — ${clinic}`}>
              {roleLabel ? `${roleLabel} · ` : ''}
              {clinic}
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
});

function isPermitted(item: NavItem, canAny: (...permissions: string[]) => boolean): boolean {
  if (!item.permission) return true;
  const keys = Array.isArray(item.permission) ? item.permission : [item.permission];
  return canAny(...keys);
}

export interface SidebarLinkProps {
  item: NavItem;
  active: boolean;
  count?: number;
  /**
   * `rail` is the 24px-tall desktop row of DS-05. `touch` is the same row at
   * DS-54's 44px, which is what the drawer on a phone renders.
   */
  density?: 'rail' | 'touch';
  /** The drawer closes behind the tap; the rail has nothing to close. */
  onNavigate?: () => void;
}

export function SidebarLink({ item, active, count, density = 'rail', onNavigate }: SidebarLinkProps) {
  const lang = useLang();
  const label = lang === 'EN' ? item.labelEn : item.labelAr;
  const Icon = item.icon;
  const urgent = Boolean(item.countKey && URGENT_COUNT_KEYS.has(item.countKey));
  const touch = density === 'touch';

  return (
    <Link
      to={item.to}
      title={label}
      data-testid={`nav-${item.key}`}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        // The transparent border on the inactive state reserves the 2px so the
        // label does not jog sideways when the route becomes active (DS-48).
        'flex items-center border-s-2 transition',
        touch ? 'min-h-touch gap-3 px-4 py-2.5 text-sm' : 'gap-2 px-3 py-1.5 text-xs',
        active
          ? 'border-blue-500 bg-blue-600/15 font-bold text-white'
          : 'border-transparent text-slate-300 hover:bg-slate-700/50',
      )}
    >
      {/* DS-45 */}
      <Icon className={cn('shrink-0', touch ? 'h-5 w-5' : 'h-4 w-4')} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-start">{label}</span>

      {count ? (
        <span
          className={cn(
            // DS-51 — a counter is a number, so it stays LTR and monospaced.
            'ltr-num shrink-0 rounded-full px-1.5 py-px font-mono text-[10px] font-bold leading-4',
            urgent ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-200',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      ) : null}
    </Link>
  );
}
